import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import URI from "urijs";

import { useUser } from "@js/apps/common/hooks/use-user";
import { ModalConfirm, ModalInstance } from "@js/components/modal";
import { useAppDispatch, useNavigate, useQueryParams } from "@js/hooks";

import { getConfirmAction } from "./config";

import styles from "./styles.module.scss";

export const useConfirmAction = () => {
  const navigate = useNavigate();
  const query = useQueryParams();
  const location = useLocation();
  const user = useUser();
  const dispatch = useAppDispatch();

  const { action, key } = query;

  const unauthenticatedNavigateToPage = useMemo(() => {
    if (user) {
      return;
    }

    return new URI("/auth/login/")
      .addSearch({
        next: `${location?.pathname}${location?.search}`,
        error_code: "confirm_action",
      })
      .toString();
  }, [user, location]);

  const confirmationConfig = useMemo(() => {
    return getConfirmAction(action as EnumType<typeof ENUMS.ConfirmAction>, {
      closeModal: ModalInstance.close,
      navigate,
      sourceLocation: location,
      dispatch,
    });
  }, [navigate, action, location, dispatch]);

  const shouldShowConfirmationModal =
    !unauthenticatedNavigateToPage && !!confirmationConfig && key;

  const wasLaunched = useRef(false);
  useEffect(() => {
    if (
      !shouldShowConfirmationModal ||
      !confirmationConfig ||
      wasLaunched.current
    ) {
      return;
    }

    wasLaunched.current = true;

    const onCancel = () => {
      ModalInstance.close();
      navigate(confirmationConfig.initialPathForAction);
    };

    const onConfirm = () => {
      void confirmationConfig.onConfirm(query);
    };

    ModalInstance.open({
      children: (
        <ModalConfirm
          onConfirm={onConfirm}
          confirmText="Confirm"
          onCancel={onCancel}
        >
          <div className="pt+">{confirmationConfig.content}</div>
        </ModalConfirm>
      ),
      keepOnBackdropClick: true,
      disableEscapeKeyDown: true,
      className: styles.modal,
      closeButton: false,
    });
  }, [
    shouldShowConfirmationModal,
    confirmationConfig,
    action,
    key,
    navigate,
    query,
  ]);

  return {
    shouldShowConfirmationModal,
    navigateTo: unauthenticatedNavigateToPage,
  };
};
